import merge from 'lodash/merge';

import type { ErrorOptions, ErrorWithRavenOptions } from './Error';

import { AjaxErrorInfo, AjaxRequestInfo } from '@egr/wcf/modules/utils/async';
import { wcfConfig } from '@egr/wcf/modules/utils';
import { CustomError } from '@egr/wcf/modules/utils/error';
import { SoapError } from '@egr/wcf/modules/eaiws';

const networkErrorQueue: Array<AjaxErrorInfo> = [];

export function retryNetworkRequests(): void {
    networkErrorQueue.forEach((error: AjaxErrorInfo): void => {
        error.retry();
    });
    networkErrorQueue.length = 0;
}

export function getQueuedNetworkErrorDetails(): Array<AjaxRequestInfo> {
    return networkErrorQueue.map((error: AjaxErrorInfo): AjaxRequestInfo => {
        return error.requestInfo;
    });
}

export type AjaxSoapErrorInfo = AjaxErrorInfo & { responseError: SoapError };

interface AjaxErrorHandlerCallbacks {
    networkError: VoidFunction;
    soapError: (error: AjaxSoapErrorInfo) => void;
    eaiwsSessionExpired: (error: AjaxSoapErrorInfo) => void;
}

function isSoapError(error: AjaxErrorInfo): error is AjaxSoapErrorInfo {
    return error.responseError instanceof SoapError;
}

export class IgnoreAjaxErrorHandlerError extends CustomError {
    constructor(message?: string, public xhr?: XMLHttpRequest) {
        super(message);

        Object.setPrototypeOf(this, IgnoreAjaxErrorHandlerError.prototype);
    }
}

export function ignoreAjaxErrorHandlerMessageCallback(error: Error | string, xhr: XMLHttpRequest, response: unknown): Error {
    const message: string  = error instanceof Error ? error.message : error.toString();
    return new IgnoreAjaxErrorHandlerError(message, xhr);
}

export function registerAjaxErrorHandler(callbacks: AjaxErrorHandlerCallbacks): void {
    if (wcfConfig.ajaxErrorHandler != null) {
        throw new Error('An global ajax error handler has already been registered');
    }

    // connect global ajax error handler
    wcfConfig.ajaxErrorHandler = (error: AjaxErrorInfo): void => {
        if (error.responseError instanceof IgnoreAjaxErrorHandlerError) {
            error.reject();
        } else if (isSoapError(error)) {
            // Note: double checking of `isinstance` vs another function argument (ts type guard)
            if (error.responseError.message.indexOf('unknown session:') === 0) {
                callbacks.eaiwsSessionExpired(error);
            } else {
                // Note: add information about the soap request
                (error.responseError as ErrorWithRavenOptions).ravenOptions = merge<
                    ErrorOptions,
                    ErrorOptions | undefined
                >(
                    {
                        extra: {
                            soapRequest: error.requestInfo.data
                        }
                    },
                    (error.responseError as ErrorWithRavenOptions).ravenOptions
                );

                callbacks.soapError(error);
            }
        } else if (error.status === 0) { // Network error
            if (error.requestInfo.url.startsWith('http:') && document.location.protocol === 'https:') {
                // mixed content
                error.reject();
            } else {
                networkErrorQueue.push(error);
                callbacks.networkError();
            }
        } else {
            error.reject();
        }
    };
}